/**
 * Login Page Component
 * Handles user authentication with AWS Cognito
 */

import React, { useState } from 'react';
import authService from '../../services/authService';
import './Auth.css';

const LoginPage = ({ onLoginSuccess }) => {
  const [mode, setMode] = useState('login'); // 'login', 'request', 'verify', 'forgot', 'newPassword'
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    newPassword: '',
    confirmPassword: '',
    name: '',
    email: '',
    verificationCode: ''
  });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [challengeData, setChallengeData] = useState(null);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await authService.signIn(formData.username, formData.password);
      
      if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setChallengeData(result);
        setMode('newPassword');
        setMessage('Please set a new password for your account');
      } else {
        setMessage('Login successful! Loading dashboard...');
        console.log('Sign in successful, waiting for session to persist...');
        // Wait longer for Cognito to write session to localStorage
        setTimeout(() => {
          console.log('Calling onLoginSuccess...');
          onLoginSuccess();
        }, 1500);
      }
    } catch (err) {
      setError(err.message || 'Failed to login');
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.newPassword.length < 12) {
      setError('Password must be at least 12 characters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Complete the new password challenge
      await authService.completeNewPassword(
        challengeData.cognitoUser,
        formData.newPassword,
        {}
      );
      
      // Sign in again with the new password to get a fresh session
      setMessage('Password set successfully! Signing in...');
      const result = await authService.signIn(formData.username, formData.newPassword);
      
      if (result && !result.challengeName) {
        // Successfully authenticated
        setMessage('Login successful! Loading dashboard...');
        console.log('Password changed and signed in, waiting for session...');
        // Wait for Cognito session to persist
        setTimeout(() => {
          console.log('Calling onLoginSuccess after password change...');
          onLoginSuccess();
        }, 1500);
      }
    } catch (err) {
      console.error('Password change error:', err);
      setError(err.message || 'Failed to set password. Please try logging in again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAccount = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await authService.requestAccount(formData.email, formData.name);
      setMessage('Account request submitted! You will receive an email once approved by the administrator.');
      setTimeout(() => setMode('login'), 3000);
    } catch (err) {
      setError(err.message || 'Failed to request account');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await authService.forgotPassword(formData.username);
      setMessage('Verification code sent to your email');
      setMode('verify');
    } catch (err) {
      setError(err.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyReset = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await authService.confirmPassword(
        formData.username,
        formData.verificationCode,
        formData.newPassword
      );
      setMessage('Password reset successfully! Please login.');
      setTimeout(() => setMode('login'), 2000);
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-box">
        <div className="auth-header">
          <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
            <img src="/images/cipheriq-logo.jpg" alt="CipherIQ" style={{ maxWidth: '300px', height: 'auto' }} />
          </div>
          <h1>🔒 CipherIQ Cyber Intelligence Platform</h1>
          <p>Secure Document Analysis & Threat Intelligence</p>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {message && (
          <div className="alert alert-success">
            {message}
          </div>
        )}

        {mode === 'login' && (
          <form onSubmit={handleLogin} className="auth-form">
            <h2>Sign In</h2>
            
            <div className="form-group">
              <label htmlFor="username">Email Address</label>
              <input
                type="email"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                placeholder="your.email@example.com"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="••••••••••••"
                required
                disabled={loading}
              />
            </div>

            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>

            <div className="auth-links">
              <button
                type="button"
                className="link-button"
                onClick={() => setMode('forgot')}
                disabled={loading}
              >
                Forgot Password?
              </button>
              <button
                type="button"
                className="link-button"
                onClick={() => setMode('request')}
                disabled={loading}
              >
                Request New Account
              </button>
            </div>
          </form>
        )}

        {mode === 'newPassword' && (
          <form onSubmit={handleNewPassword} className="auth-form">
            <h2>Set New Password</h2>
            <p className="form-subtitle">Your temporary password has expired. Please set a new password.</p>
            
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleInputChange}
                placeholder="Minimum 12 characters"
                required
                disabled={loading}
              />
              <small>Must contain uppercase, lowercase, numbers, and symbols</small>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Re-enter password"
                required
                disabled={loading}
              />
            </div>

            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Setting Password...' : 'Set Password'}
            </button>
          </form>
        )}

        {mode === 'request' && (
          <form onSubmit={handleRequestAccount} className="auth-form">
            <h2>Request Account</h2>
            <p className="form-subtitle">Submit your information for admin approval</p>
            
            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="John Doe"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="john.doe@company.com"
                required
                disabled={loading}
              />
            </div>

            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Request'}
            </button>

            <div className="auth-links">
              <button
                type="button"
                className="link-button"
                onClick={() => setMode('login')}
                disabled={loading}
              >
                Back to Sign In
              </button>
            </div>
          </form>
        )}

        {mode === 'forgot' && (
          <form onSubmit={handleForgotPassword} className="auth-form">
            <h2>Reset Password</h2>
            <p className="form-subtitle">Enter your email to receive a verification code</p>
            
            <div className="form-group">
              <label htmlFor="username">Email Address</label>
              <input
                type="email"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                placeholder="your.email@example.com"
                required
                disabled={loading}
              />
            </div>

            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Sending...' : 'Send Verification Code'}
            </button>

            <div className="auth-links">
              <button
                type="button"
                className="link-button"
                onClick={() => setMode('login')}
                disabled={loading}
              >
                Back to Sign In
              </button>
            </div>
          </form>
        )}

        {mode === 'verify' && (
          <form onSubmit={handleVerifyReset} className="auth-form">
            <h2>Reset Password</h2>
            <p className="form-subtitle">Enter the verification code sent to your email</p>
            
            <div className="form-group">
              <label htmlFor="verificationCode">Verification Code</label>
              <input
                type="text"
                id="verificationCode"
                name="verificationCode"
                value={formData.verificationCode}
                onChange={handleInputChange}
                placeholder="123456"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleInputChange}
                placeholder="Minimum 12 characters"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Re-enter password"
                required
                disabled={loading}
              />
            </div>

            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>

            <div className="auth-links">
              <button
                type="button"
                className="link-button"
                onClick={() => setMode('login')}
                disabled={loading}
              >
                Back to Sign In
              </button>
            </div>
          </form>
        )}

        <div className="auth-footer">
          <p>Administrator: {formData.username === 'dwatkins@aperioglobal.com' ? '👑 Admin' : 'dwatkins@aperioglobal.com'}</p>
          <p className="security-note">🔒 Secured by AWS Cognito</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
